<template>
  <div>
    <div v-if="DossiersOrTexts === 'Dossiers'">
      <v-data-table-server
        :headers="dossierHeaders"
        :items="dossiersInfo"
        :items-length="totalDossiers"
        :page="page"
        :items-per-page="rows"
        :items-per-page-options="[10, 15, 25, 50]"
        @update:page="$emit('update:page', $event)"
        @update:items-per-page="$emit('update:rows', $event)"
      >
        <template #[`item.name`]="{ item }">
          <router-link :to="`/dossier/${item.uuid}`">{{
            item.name
          }}</router-link>
        </template>

        <template #[`item.numTexts`]="{ item }">
          {{ item.totalTexts }}
        </template>
      </v-data-table-server>
    </div>
    <div v-else>
      <v-data-table-server
        :headers="textHeaders"
        :items="texts"
        :items-length="totalTexts"
        :page="page"
        :items-per-page="rows"
        :items-per-page-options="[10, 25, 50, 100]"
        @update:page="$emit('update:page', $event)"
        @update:items-per-page="$emit('update:rows', $event)"
      >
        <template #[`item.name`]="{ item }">
          <router-link v-if="item.uuid" :to="`/epigraphies/${item.uuid}`"
            >{{ item.name }}
          </router-link>
          <span v-else>{{ item.name }}</span>
        </template>

        <template #[`item.excavation`]="{ item }">
          {{ item.excavationPrefix }} {{ item.excavationNumber }}
        </template>

        <template #[`item.museum`]="{ item }">
          {{ item.museumPrefix }} {{ item.museumNumber }}
        </template>

        <template #[`item.publication`]="{ item }">
          {{ item.publicationPrefix }} {{ item.publicationNumber }}
        </template>

        <template v-if="isAdmin" #[`item.removeText`]="{ item }">
          <v-btn class="disconnect-btn" @click="selectItemForRemoval(item)" icon
            ><v-icon>mdi-note-remove-outline</v-icon></v-btn
          >
        </template>
      </v-data-table-server>
    </div>
    <oare-dialog
      v-model="isRemoving"
      title="Disconnect text"
      submitText="Yes"
      cancelText="No"
      :persistent="false"
      @submit="disconnectText"
      >Disconnect text {{ itemToDisconnect.name }} from archive?</oare-dialog
    >
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import { Text, DossierInfo } from '@oare/types';
import { DataTableHeader } from 'vuetify';
import sl from '@/serviceLocator';

export default defineComponent({
  name: 'ArchiveTextsDossiers',
  props: {
    texts: {
      type: Array as PropType<Text[]>,
      required: true,
    },
    dossiersInfo: {
      type: Array as PropType<DossierInfo[]>,
      required: true,
    },
    DossiersOrTexts: {
      type: String,
      required: true,
    },
    totalTexts: {
      type: Number,
      required: true,
    },
    totalDossiers: {
      type: Number,
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
    rows: {
      type: Number,
      default: 10,
    },
    archiveUuid: {
      type: String,
      required: true,
    },
  },
  emits: ['refresh-page', 'update:page', 'update:rows'],
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');

    const dossierHeaders = ref<DataTableHeader[]>([]);
    const itemToDisconnect = ref<Text>({
      uuid: '',
      type: '',
      name: '',
      excavationPrefix: '',
      excavationNumber: '',
      museumPrefix: '',
      museumNumber: '',
      publicationPrefix: '',
      publicationNumber: '',
    });
    const isRemoving = ref(false);

    const textHeaders = computed(() => {
      const headers: DataTableHeader[] = [
        {
          title: 'Text Name',
          key: 'name',
          width: '30%',
        },
        {
          title: 'Excavation Info',
          key: 'excavation',
          width: '20%',
        },
        {
          title: 'Museum Info',
          key: 'museum',
          width: '20%',
        },
        {
          title: 'Primary Publication Info',
          key: 'publication',
          width: '20%',
        },
      ];
      if (isAdmin.value) {
        headers.push({
          title: 'Remove Text',
          key: 'removeText',
          sortable: false,
          width: '10%',
        });
      }
      return headers;
    });

    dossierHeaders.value = [
      {
        title: 'Dossier Name',
        key: 'name',
        width: '70%',
      },
      {
        title: 'Number of Texts',
        key: 'numTexts',
        width: '30%',
      },
    ];

    const isAdmin = computed(() => store.getters.isAdmin);

    const selectItemForRemoval = (item: Text) => {
      itemToDisconnect.value = item;
      isRemoving.value = true;
    };

    const disconnectText = async () => {
      try {
        await server.disconnectText({
          textUuid: itemToDisconnect.value.uuid,
          archiveOrDossierUuid: props.archiveUuid,
        });
        actions.showSnackbar(
          `Successfully disconnected ${itemToDisconnect.value.name} from archive.`
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Error removing text. Please try again.',
          err as Error
        );
      } finally {
        isRemoving.value = false;
        itemToDisconnect.value = {
          uuid: '',
          type: '',
          name: '',
          excavationPrefix: '',
          excavationNumber: '',
          museumPrefix: '',
          museumNumber: '',
          publicationPrefix: '',
          publicationNumber: '',
        };
        emit('refresh-page');
      }
    };

    return {
      textHeaders,
      dossierHeaders,
      selectItemForRemoval,
      disconnectText,
      isRemoving,
      itemToDisconnect,
      isAdmin,
    };
  },
});
</script>
